import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)

export const POLL_INTERVAL = 1500
export const POLL_TIMEOUT = 5 * 60 * 1000

const LONG_TEXT_FIELDS = ['context', 'company_summary']
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function baseType(prop) {
  const types = [].concat(prop.type || [])
  return types.find(type => type !== 'null')
}

function fieldKind(name, prop) {
  const type = baseType(prop)
  if (prop.enum) return 'enum'
  if (type === 'array') return prop.items?.enum ? 'multienum' : 'tags'
  if (type === 'integer' || type === 'number') return 'number'
  if (prop.format === 'date') return 'date'
  if (prop.format === 'uuid') return 'uuid'
  if (LONG_TEXT_FIELDS.includes(name)) return 'textarea'
  return 'text'
}

export function makeToolFields(input_schema) {
  const required = input_schema?.required || []
  return Object.entries(input_schema?.properties || {}).map(([name, prop]) => {
    const kind = fieldKind(name, prop)
    const enum_values = kind === 'multienum' ? prop.items.enum : prop.enum
    return {
      name,
      kind,
      type: baseType(prop),
      required: required.includes(name),
      description: prop.description,
      min_items: prop.minItems,
      max_items: prop.maxItems,
      items: (enum_values || []).map(value => ({value, title: String(value), label: String(value)}))
    }
  })
}

export function isEmpty(value) {
  return value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)
}

export function fieldsToArgs(fields, form) {
  const args = {}
  for (const field of fields) {
    const value = form[field.name]
    if (isEmpty(value)) continue
    args[field.name] = field.kind === 'number' ? Number(value) : value
  }
  return args
}

export function argsToForm(fields, args) {
  const form = {}
  for (const field of fields) {
    const value = args?.[field.name]
    if (['tags', 'multienum'].includes(field.kind)) form[field.name] = Array.isArray(value) ? [...value] : []
    else if (field.kind === 'number') form[field.name] = isEmpty(value) ? undefined : String(value)
    else form[field.name] = value ?? undefined
  }
  return form
}

export function fieldError(field, value) {
  if (isEmpty(value)) return field.required ? 'Required' : undefined
  if (field.kind === 'uuid' && !UUID_RE.test(value)) return 'Must be a valid UUID'
  if (field.kind === 'number') {
    if (isNaN(Number(value))) return 'Must be a number'
    if (field.type === 'integer' && !Number.isInteger(Number(value))) return 'Must be an integer'
  }
  if (Array.isArray(value)) {
    if (field.min_items !== undefined && value.length < field.min_items) return `At least ${field.min_items} items`
    if (field.max_items !== undefined && value.length > field.max_items) return `At most ${field.max_items} items`
  }
}

export function elapsedSeconds(elapsed) {
  return dayjs.duration(elapsed).asSeconds()
}

export function errorText(err) {
  const message = err?.json?.message
  if (Array.isArray(message)) return message.join(', ')
  if (message) return String(message)
  return err?.message || String(err)
}
